import { PhoneNumberResponse, WhatsAppBusinessAccountResponse } from '@trendbuild/trend-cloud-api';
import { Base } from './base.model';
import { ChannelHealth } from './channel-health.model';
import { Company } from './company.model';
import { ChannelTypeEnum } from './enum/channel.type';
import { User } from './user.model';

export interface Channel extends Base {
  name: string;
  description: string;
  type: ChannelTypeEnum;
  identifier: string;
  metadata: MetadataChannel;
  credentials: CredentialsChannel;
  company: Company;
  connected: boolean;
  activated: boolean;
  apis: ChannelAPIs;
  removed: boolean;
  lastSyncDate?: Date;
  assigned: boolean; // trial or paid
  campaignOnly: boolean; // only for campaign use
  creditLine?: boolean; // CLOUD_API only: runs on the Solution Partner's credit line (set at creation, immutable)
  /** CLOUD_API only: Hybrid channel — official + session on the same number (set at creation, immutable, never with creditLine). */
  hybrid?: boolean;
  /** Hybrid official channel only: routing share and the session side's state. */
  hybridConfig?: HybridConfig;
  /** Latest provider health reading — Cloud API only (SPEC-2026-08-channel-health). */
  health?: ChannelHealth;
}

/** A Hybrid ("Split") channel: the official side of a Cloud API number paired with a session (SPEC-2026-10-whatsapp-hybrid-channel). */
export const isHybridChannel = (channel?: Pick<Channel, 'type' | 'hybrid'> | null): boolean =>
  channel?.type === ChannelTypeEnum.CLOUD_API && channel?.hybrid === true;

export interface ChannelEmbedded {
  name: string;
  phoneNumberID?: string;
  whatsAppBusinessAccountID?: string;
  businessID?: string;
  code?: string;
  /** Embedded Signup event that finished the flow; a Hybrid channel requires the coexistence one. */
  finishEvent?: string;
}

/** Embedded Signup event of a coexistence (WhatsApp Business app) onboarding. */
export const EMBEDDED_SIGNUP_COEXISTENCE_FINISH = 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING';

export interface ChannelAPIs {
  uuid?: string;
  token?: string;
  createdAt?: Date;
}

export type MetadataChannel = Partial<MetadataCloudAPI & MetadataInstagramAPI & MetadataMessengerAPI & MetadataRCSAPI & MetadataVoipAPI & MetadataWebchatAPI>;

export type CredentialsChannel = Partial<CredentialsInfoChannel & CredentialsCloudAPI & CredentialsZAPI & CredentialsInstagramAPI & CredentialsMessengerAPI & CredentialsRCSAPI & CredentialsVoipAPI>;

export interface CredentialsVoipAPI {
  server?: string;
  port?: number;
  username?: string;
  secret?: string;
}

export interface MetadataVoipAPI {
  defaultExtension?: string;
  extensions: Array<VoipExtension>;
}

export interface VoipExtension {
  user: User;
  sipUsername?: string;
  sipPassword?: string;
  activated: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface VoipIceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export interface VoipExtensionCredentials {
  sipUsername: string;
  sipPassword: string;
  server: string;
  port: number;
  iceServers?: VoipIceServer[];
}

// Webchat widget configuration (SPEC-2026-07-webchat-channel).
// The embed identity reuses Channel.apis.uuid — no credentials variant needed.
export interface MetadataWebchatAPI {
  primaryColor?: string;
  logoUrl?: string;
  buttonPosition?: 'left' | 'right';
  authorizedDomains?: Array<string>;
  privacyPolicyUrl?: string;
}

export interface CredentialsInfoChannel {
  url?: string;
  version?: string;
}

export interface CredentialsCloudAPI {
  token: string;
  phoneNumberID: string;
  whatsAppBusinessAccountID: string;
}

export interface CredentialsZAPI {
  id?: string;
  name?: string;
  token?: string;
}

export interface CredentialsInstagramAPI {
  idPageFacebook?: string;
  tokenFacebook?: string;
  instagramBusinessAccountID?: string;
  clientID?: string;
  clientSecret?: string;
}

export interface CredentialsMessengerAPI {
  id?: string;
  token?: string;
  clientID?: string;
  clientSecret?: string;
}

/**
 * The account is the ONLY RCS credential the frontend knows about.
 *
 * `clientSecret` / `accessToken` still exist on the backend entity, but they are one token for
 * the whole Pontal organization: the API stamps the secret from its own environment on every
 * save, and no screen ever collects or displays it. Leaving them out here is what keeps a form
 * from quietly re-introducing the field.
 */
export interface CredentialsRCSAPI {
  account?: string;
}

export interface MetadataCloudAPI {
  token: string;
  phoneNumberID: string;
  whatsAppBusinessAccountID: string;
  phoneNumberInfo: PhoneNumberResponse;
  whatsAppBusinessAccountInfo: WhatsAppBusinessAccountResponse;
}

export interface MetadataInstagramAPI {
  id: string;
  token: string;
  name: string;
  username: string;
  instagramBusinessAccountID: string;
}

export interface MetadataMessengerAPI {
  id?: string;
  token?: string;
  name: string;
}

export interface MetadataRCSAPI {
  account?: string;
  clientSecret?: string;
  accessToken?: string;
  /**
   * The provider account is provisioned as conversational, so it can be used in
   * attendance. Absent or false means a campaign-only account.
   *
   * Never read directly — resolve it through `isRCSConversational()`.
   */
  conversational?: boolean;
}

/** Share of free-form messages carried by the official side of a Hybrid channel (SPEC-2026-10-whatsapp-hybrid-channel RF-26). */
export type HybridOfficialShare = 25 | 50 | 75 | 100;

export const HYBRID_OFFICIAL_SHARES: ReadonlyArray<HybridOfficialShare> = [25, 50, 75, 100];

export interface HybridConfig {
  officialShare: HybridOfficialShare;
  sessionChannel?: string;
  sessionConnected?: boolean;
  sessionLastSeenAt?: Date | string;
  sessionMismatch?: boolean;
}

/** Answer of `POST /channel/hybrid/:idChannel/session` — pairing the session side (RF-02). */
export interface HybridSessionPairing {
  sessionChannel: string;
  /** QR payload (rendered with <qrcode>) to scan with the business app; absent once connected. */
  qrCode?: string;
  connected: boolean;
}

/** Answer of `GET /channel/hybrid-usage/:idChannel` (RF-25). */
export interface HybridUsageResponse {
  month: string;
  outbound: { official: number; session: number };
  freeForm: { official: number; session: number; fallback: number };
  templates: number;
  effectiveShare: number | null;
  configuredShare: HybridOfficialShare;
  estimatedSavingUSD: number;
  rateUSD: number;
  rateSource: 'BUSINESS_ACCOUNT' | 'REFERENCE';
}
